import { makeAutoObservable, runInAction } from 'mobx'
import type { IIconsVector } from 'shared/ui'
import { logger } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import { ConversationsApi, type IResponseConversationsQuickFilterKey } from 'entities/Conversation'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { eventEmitterStore } from 'entities/EventEmitter'
import type {
  IConversationsQuickFilter,
  IQuickFilterCard,
  IUpdateQuickFilterCountByKeyProps,
  IUpdateQuickFilterCountProps,
  IUpdateQuickFilterDraftCountProps,
} from 'pages/conversations/ui/ConversationMenu/store/types'
import { ConversationsPath } from 'pages/conversations/route/conversationsPath'
import { type ConversationMenuStore } from 'pages/conversations/ui/ConversationMenu'

export class QuickFiltersMenuStore {
  private _quickFiltersMap: Map<IResponseConversationsQuickFilterKey, IConversationsQuickFilter> =
    new Map()
  private _disabledButton = false

  constructor(
    private _conversationMenuStore: ConversationMenuStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  initInboxQuickFilter = async () => {
    this._conversationMenuStore.setLoadingUser(true)

    await this._entities.usersStore.initPromise

    this._entities.inboxesStore.localInboxes.initInboxQuickFilter()

    this._conversationMenuStore.setLoadingUser(false)
  }

  initQuickFilters = async () => {
    try {
      const { data } = await ConversationsApi.getConversationsQuickFilters({
        platform: 'web',
      })

      data.forEach((item) => {
        const currentQuickFilter: IConversationsQuickFilter = {
          key: item.key,
          label: item.label,
          count: item.count,
          conversation_ids: new Set(item.conversation_ids || []),
        }

        runInAction(() => {
          this._quickFiltersMap.set(item.key, currentQuickFilter)
        })
      })
    } catch (e) {
      logger.error(e)
    }
  }

  handleQuickFilter = async (activeFilterKey: IResponseConversationsQuickFilterKey) => {
    const filterKey = activeFilterKey === 'all' ? 'open' : activeFilterKey

    runInAction(() => {
      this._disabledButton = true
    })

    this.config?.resetConversations?.(false)
    this.config?.resetConversationsReactions?.()
    this.config?.setConversationsLoading?.(true)
    this._entities.conversationStore.setCurrentItemId(null)
    this._entities.inboxesStore.localInboxes.setCurrentInbox(activeFilterKey)

    await this._conversationMenuStore.handleUpdateTeamInbox(0)

    if (activeFilterKey === 'calls') {
      uiStore.changeRoute({
        path: `/${ConversationsPath.calls}`,
      })
    }

    if (activeFilterKey !== 'calls') {
      uiStore.changeRoute({
        path: `/${ConversationsPath.root}`,
      })
      eventEmitterStore.emit('initConversationsList')
      eventEmitterStore.emit('updateConversationsFilter', { id: filterKey })
    }

    runInAction(() => {
      this._disabledButton = false
    })
  }

  handleQuickFilterCountByKey = ({
    key,
    isIncrease,
    increaseCount,
    decreaseCount = 0,
  }: IUpdateQuickFilterCountByKeyProps) => {
    const quickFilter = this._quickFiltersMap.get(key)

    if (quickFilter) {
      if (isIncrease && increaseCount) {
        quickFilter.count += increaseCount

        this._quickFiltersMap.set(key, quickFilter)
        return
      }

      quickFilter.count = isIncrease
        ? quickFilter.count + 1
        : quickFilter.count >= decreaseCount
        ? quickFilter.count - decreaseCount
        : 0

      this._quickFiltersMap.set(key, quickFilter)
    }
  }

  updateCountCalls = () => {
    const item = this._quickFiltersMap.get('calls')
    const unifyInbox = this._entities.inboxesStore.unifiedInboxes[0]

    if (item && unifyInbox) {
      this._quickFiltersMap.set('calls', {
        ...item,
        count: unifyInbox.unread_calls,
      })
    }
  }

  handleQuickFilterDraftCount = ({
    conversationId,
    isIncrease,
    decreaseCount,
  }: IUpdateQuickFilterDraftCountProps) => {
    const key = 'draft'
    const quickFilterDraft = this._quickFiltersMap.get(key)

    if (
      (quickFilterDraft?.conversation_ids.has(conversationId) && isIncrease) ||
      (!quickFilterDraft?.conversation_ids.has(conversationId) && !isIncrease && decreaseCount)
    ) {
      return
    }

    if (quickFilterDraft?.conversation_ids.has(conversationId) && !isIncrease) {
      quickFilterDraft.conversation_ids.delete(conversationId)
      quickFilterDraft.count = quickFilterDraft.conversation_ids.size
      this._quickFiltersMap.set(key, quickFilterDraft)
    }

    if (quickFilterDraft && !quickFilterDraft.conversation_ids.has(conversationId) && isIncrease) {
      quickFilterDraft.conversation_ids.add(conversationId)
      quickFilterDraft.count = quickFilterDraft.conversation_ids.size
      this._quickFiltersMap.set(key, quickFilterDraft)
    }
  }

  handleQuickFilterCount = ({
    conversation,
    isIncrease,
    decreaseCount,
  }: IUpdateQuickFilterCountProps) => {
    if (conversation.hasAssignedFilter) {
      this.handleQuickFilterCountByKey({
        key: 'assigned',
        isIncrease,
        decreaseCount,
      })
    }

    if (conversation.hasUnassignedFilter) {
      this.handleQuickFilterCountByKey({
        key: 'unassigned',
        isIncrease,
        decreaseCount,
      })
    }

    this.handleQuickFilterCountByKey({
      key: 'all',
      isIncrease,
      decreaseCount,
    })
  }

  get quickFilters(): IQuickFilterCard[] {
    return Array.from(this._quickFiltersMap.values()).map((item) => {
      let icon: IIconsVector = 'conversation'

      if (item.key === 'calls') icon = 'phone'
      if (item.key === 'assigned') icon = 'userFilled'
      if (item.key === 'mentions') icon = 'at'
      if (item.key === 'unassigned') icon = 'userFilled'
      if (item.key === 'draft') icon = 'edit'

      return {
        id: item.key,
        label: item.label,
        iconL: icon,
        count: item.count,
        active:
          this.currentInboxQuickFilter === item.key && this._entities.inboxesStore.isUnifiedInbox,
      }
    })
  }

  get currentInboxQuickFilter() {
    return this._entities.inboxesStore.localInboxes.currentInboxId
  }

  get config() {
    return this._conversationMenuStore.config
  }

  get disabledButton() {
    return this._disabledButton
  }
}
